import { Component, ErrorInfo, ReactNode } from 'react'
import * as Sentry from '@sentry/react'
import { AlertTriangle, RefreshCw, Home } from 'lucide-react'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  }

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null }
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo)

    this.setState({
      error,
      errorInfo,
    })

    Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } })
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null })
    window.location.href = '/dashboard'
  }

  private handleReload = () => {
    window.location.reload()
  }

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-framed-neutral-50 flex items-center justify-center p-4">
          <div className="max-w-2xl w-full bg-white rounded-xl shadow-lg p-8">
            {/* Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
            </div>

            {/* Title */}
            <h1 className="text-2xl font-bold text-framed-neutral-900 text-center mb-3">
              Something went wrong
            </h1>

            {/* Description */}
            <p className="text-framed-neutral-600 text-center mb-6">
              The application encountered an unexpected error.
              Try reloading the page or go back to the dashboard.
            </p>

            {/* Error details (only in development) */}
            {import.meta.env.DEV && this.state.error && (
              <details className="mb-6 bg-framed-neutral-50 rounded-lg p-4">
                <summary className="cursor-pointer font-semibold text-framed-neutral-700 mb-2">
                  Technical details (only visible in development)
                </summary>
                <div className="mt-3 space-y-2">
                  <div>
                    <p className="text-sm font-semibold text-framed-neutral-700">Error:</p>
                    <pre className="text-xs text-red-600 bg-red-50 p-3 rounded mt-1 overflow-x-auto">
                      {this.state.error.toString()}
                    </pre>
                  </div>
                  {this.state.errorInfo && (
                    <div>
                      <p className="text-sm font-semibold text-framed-neutral-700">Component Stack:</p>
                      <pre className="text-xs text-framed-neutral-600 bg-framed-neutral-100 p-3 rounded mt-1 overflow-x-auto">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </div>
                  )}
                </div>
              </details>
            )}

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={this.handleReload}
                className="btn btn-primary flex items-center justify-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Reload page
              </button>
              <button
                onClick={this.handleReset}
                className="btn btn-secondary flex items-center justify-center gap-2"
              >
                <Home className="w-4 h-4" />
                Go to Dashboard
              </button>
            </div>

            {/* Help text */}
            <p className="text-sm text-framed-neutral-500 text-center mt-6">
              Does this problem persist? Contact support.
            </p>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
